import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getSelectedBudget } from 'state/BudgetList/budgetListSlice';
import { BudgetSourceEnum, Option } from 'utils/customTypes';
import { selectAllPrograms } from '../../../state/Programs/programsSlice';
import { selectAllProjects } from '../../../state/Projects/projectsSlice';
import { selectLearningTeams } from '../../../state/Teams/teamsSlice';
import { selectVendors } from '../../../state/Vendors/vendorsSlice';
import { naturalSort } from 'utils/naturalSorting';
const useOptions = () => {
  const currentBudget = useSelector(getSelectedBudget);
  const projects = useSelector(selectAllProjects);
  const learningTeams = useSelector(selectLearningTeams);
  const vendors = useSelector(selectVendors);
  const programs = useSelector(selectAllPrograms);

  const filteredTeams = useMemo(() => {
    return naturalSort([...learningTeams], 'name', 'asc');
  }, [learningTeams]);

  const filteredVendors = useMemo(() => {
    return naturalSort([...vendors], 'vendorName', 'asc');
  }, [vendors]);

  const filteredPrograms = useMemo(() => {
    let newFilteredPrograms = [...programs];
    if (currentBudget) {
      const budgetStartDate = new Date(currentBudget.fiscalPeriodStart);
      const budgetEndDate = new Date(budgetStartDate);
      budgetEndDate.setFullYear(budgetEndDate.getFullYear() + 1);

      newFilteredPrograms = newFilteredPrograms.map((program) => ({
        ...program,
        programProjects: program.programProjects
          ? program.programProjects.filter((project) => {
              const projectStartDate = new Date(project.startDate);
              const projectEndDate = project.targetCompletionDate
                ? new Date(project.targetCompletionDate)
                : null;

              return (
                projectStartDate <= budgetEndDate &&
                (projectEndDate ? projectEndDate >= budgetStartDate : true)
              );
            })
          : [],
      }));
    }
    return naturalSort([...newFilteredPrograms], 'title', 'asc');
  }, [programs, currentBudget]);

  const filteredProjects = useMemo(() => {
    let newFilteredProjects = [...projects];
    if (currentBudget) {
      const budgetStartDate = new Date(currentBudget.fiscalPeriodStart);
      const budgetEndDate = new Date(budgetStartDate);
      budgetEndDate.setFullYear(budgetEndDate.getFullYear() + 1);

      newFilteredProjects = projects.filter((project) => {
        const projectStartDate = new Date(project.startDate);
        const projectEndDate = project.targetCompletionDate
          ? new Date(project.targetCompletionDate)
          : null;

        return (
          projectStartDate <= budgetEndDate &&
          (projectEndDate ? projectEndDate >= budgetStartDate : true)
        );
      });
    }

    return naturalSort([...newFilteredProjects], 'title', 'asc');
  }, [projects, currentBudget]);

  const projectOptions = useMemo(() => {
    return filteredProjects.map((project) => ({
      label: project.title,
      value: project.id,
    }));
  }, [filteredProjects]);

  const budgetSourceOptions = useMemo<Option[]>(() => {
    return Object.values(BudgetSourceEnum).map((value) => ({
      value,
      label: value,
    }));
  }, []);

  const vendorOptions = useMemo(
    () =>
      filteredVendors.map((vendor) => ({
        label: vendor.vendorName,
        value: vendor.id,
      })),
    [filteredVendors]
  );

  const learningTeamOptions = useMemo(
    () =>
      filteredTeams.map((team) => ({
        label: team.name,
        value: team.id,
      })),
    [filteredTeams]
  );

  const programOptions: Option[] = useMemo(
    () =>
      filteredPrograms.map((program) => ({
        label: program.title,
        value: program.id,
      })),
    [filteredPrograms]
  );

  const learningTeamLookup = Object.fromEntries(
    learningTeamOptions.map((team) => [team.value, team.label])
  );

  const programLookup = Object.fromEntries(
    programOptions.map((program) => [program.value, program.label])
  );

  const projectLookup = Object.fromEntries(
    projectOptions.map((project) => [project.value, project.label])
  );

  const vendorLookup = Object.fromEntries(
    vendorOptions.map((vendor) => [vendor.value, vendor.label])
  );

  return {
    programOptions,
    projectOptions,
    vendorOptions,
    learningTeamOptions,
    budgetSourceOptions,
    filteredPrograms,
    filteredProjects,
    filteredTeams,
    filteredVendors,
    learningTeamLookup,
    programLookup,
    projectLookup,
    vendorLookup,
  };
};

export default useOptions;
